import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "../Images/delete.png";
import { getSelectDropdownUI } from "../HelperFunctions/EmployeeFormHelpers";
import { ITEM_TYPE_MAP } from "../Enums/Enum";
import { getPayrollItemTypeMapping, updatePayrollItemTypeMapping, getPayPreferenceItemTypeMapping } from "../api";
import { useSnackbar } from "notistack";
import PayrollsPopup from "./PayrollPopup";
import { useAlerts } from "../Hooks";
import { Loader } from "./Loader";

// --- Style Constants ---
const cellBorderStyle = {
  borderBottom: "1px solid #f2f2f2",
  borderRight: "1px solid #f2f2f2",
  padding: "6px 8px",
};

const tableHeaderCellSx = { 
  background: "linear-gradient(to bottom, #e6e6e6 0%, #f2f2f2 50%, #e6e6e6 100%)",
  fontWeight: 500, 
  fontSize: "0.75rem", 
  color: "#000",
  ...cellBorderStyle, 
  textAlign: 'center' 
};

const tableBodyCellSx = { 
  fontSize: "0.7rem", 
  fontWeight: 500,
  color: "#626262",
  ...cellBorderStyle 
};

const menuItemSx = { fontSize: "0.7rem" };

// --- Helper Functions & Constants ---
const EXCLUDE_OPTION = "Exclude";



const mapInitialItems = (unmappedItems = []) => {
  return unmappedItems.map((item, index) => {
    const nimbleMapping = item.ItemType === 13 ? EXCLUDE_OPTION : (ITEM_TYPE_MAP[item.ItemType] || "");
    return {
      id: `item-${index}`,
      payItemTypeId: item.PayItemTypeID || null, // Original ID from the server
      itemName: item.ImportedItemName,
      aiType: item.ImportedItemType,
      nimbleMapping: nimbleMapping,
    };
  });
};

const PayrollItemTypeMapping = ({ open, onClose, onUpdateAndNext, source, rowData }) => {
  const [itemsToMap, setItemsToMap] = useState([]);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { popup: alertPopup, showDeleteConfirm, hidePopup } = useAlerts();
  const isPayrollEntry = source === 'PayrollEntry';
  
  useEffect(() => {
    if (open) {
      let isActive = true;

      const fetchMapping = async () => {
        setLoading(true);
        try {
          let response;
          if (isPayrollEntry && rowData?.id) {
            response = await getPayrollItemTypeMapping(rowData.id);
          } else {
            response = await getPayPreferenceItemTypeMapping();
          }

          if (isActive && response?.StatusCode === 200 && response.payItemMap) {
            setItemsToMap(mapInitialItems(response.payItemMap));
          }
        } catch (error) {
          console.error("Error fetching payroll item type mapping:", error);
        } finally {
          if (isActive) {
            setLoading(false);
          }
        }
      };

      fetchMapping();
      return () => {
        isActive = false;
      };
    } else {
      setItemsToMap([]); // Reset state on close
    }
  }, [open, isPayrollEntry, rowData]);

  const handleMappingChange = useCallback((itemId, newValue) => {
    setItemsToMap(prevItems =>
      prevItems.map(item =>
        item.id === itemId ? { ...item, nimbleMapping: newValue } : item
      )
    );
  }, []);
  const handleDeleteItem = useCallback((itemId) => {    
    showDeleteConfirm("Do you want to delete the row?", () => {
      setItemsToMap(prevItems => prevItems.filter(item => item.id !== itemId));
    });
  }, [showDeleteConfirm]);

  const reverseItemTypeMap = useMemo(() => new Map(
    Object.entries(ITEM_TYPE_MAP).map(([id, name]) => [name, Number(id)])
  ), []);

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const payload = {
        IsItemTypeSave: isPayrollEntry ? true : false,
        payItemMap: itemsToMap.map(item => {
          const isExcluded = item.nimbleMapping === EXCLUDE_OPTION;
          const itemType = isExcluded ? 13 : (reverseItemTypeMap.get(item.nimbleMapping) || 0);

          return {
            PayItemTypeID: item.payItemTypeId,
            ImportedItemName: item.itemName,
            ImportedItemType: item.aiType,
            ItemType: itemType,
            // IsActive: isExcluded ? 0 : 1,
            IsActive: 1,
            MapListCount : !isPayrollEntry ? itemsToMap.length : 0,
          };
        })
      };

      const response = await updatePayrollItemTypeMapping(payload);
      if (response?.StatusCode === 200) {
        enqueueSnackbar("Payroll item mappings updated successfully.", { variant: "success" });
        onUpdateAndNext(rowData);
      } else {
        enqueueSnackbar(response?.Message || "Failed to update mappings.", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar(error?.message || "An error occurred while updating.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const memoizedMenuItems = useMemo(() => [
    ...Object.entries(ITEM_TYPE_MAP).map(([id, name]) => (
      <MenuItem key={id} value={name} sx={menuItemSx}>{name}</MenuItem>
    )),
    <MenuItem key="exclude" sx={menuItemSx} value={EXCLUDE_OPTION}>{EXCLUDE_OPTION}</MenuItem>
  ], []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth      
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
        },
      }}
      PaperProps={{
        sx: {
          p: 1,
          bgcolor: '#F8F9FA',
          height: { xs: '90vh', md: '70vh' },
          maxHeight: '90vh', // Keep a hard limit
          mt: 4, // Margin from the top
          display: "flex",
          flexDirection: "column"
        }
      }}      
    >
      <PayrollsPopup open={alertPopup.open} popupType={alertPopup.type} message={alertPopup.message} onConfirm={alertPopup.onConfirm} onCancel={hidePopup} />
        {loading && <Loader loading={loading} />}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, }}>
        <Typography variant="h6" sx={{ fontSize: "15px", fontWeight: 500, color: "#2782EC" }}>
          {isPayrollEntry ? "Payroll Item type Mapping - New line(s) identified" : "Payroll Item Type Mapping"}
        </Typography>
        <IconButton
          onClick={onClose}
          disableRipple
          sx={{
            width: 16,
            height: 16,
            borderRadius: "2px",
            backgroundColor: "#8c8c8c",
            color: "#fff",
            p: 0,
            "&:hover": {
              backgroundColor: "#7a7a7a",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: "14px" }} />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 1, flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
        <TableContainer component={Box} sx={{ height: "100%", overflow: 'auto', border: "1px solid #f2f2f2", borderRadius: 1, bgcolor: "white" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeaderCellSx}>Item Name - As per file</TableCell>
                <TableCell sx={tableHeaderCellSx}>AI Type</TableCell>
                <TableCell sx={tableHeaderCellSx}>Nimble Mapping</TableCell>
                {!isPayrollEntry && (
                  <TableCell sx={{...tableHeaderCellSx, borderRight: "none"}}>Action</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {itemsToMap.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell sx={{ ...tableBodyCellSx, pl: 1 }} title={item.itemName}>{item.itemName}</TableCell>
                  <TableCell sx={tableBodyCellSx} title={item.aiType}>{item.aiType}</TableCell>
                  <TableCell sx={tableBodyCellSx}>
                    <Select
                      size="small"
                      fullWidth
                      value={item.nimbleMapping}
                      onChange={(e) => handleMappingChange(item.id, e.target.value)}
                      displayEmpty
                      MenuProps={getSelectDropdownUI()}
                      sx={{ fontSize: "0.7rem", height: 30, bgcolor: "#fff" }}
                    >
                      {memoizedMenuItems}
                    </Select>
                  </TableCell>
                  {!isPayrollEntry && (
                    <TableCell sx={{ ...tableBodyCellSx, textAlign: 'center', borderRight: "none" }}>
                      <IconButton size="small" onClick={() => handleDeleteItem(item.id)} aria-label={`Delete item ${item.itemName}`} >
                        <img
                            src={DeleteIcon}
                            alt="delete"
                            width={18}
                            height={18}
                        />
                        </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ p: '8px 16px', flexShrink: 0, bgcolor: '#F8F9FA' }}>
        <Button
          onClick={onClose}
          variant="contained"
          size="small"
          sx={{
            backgroundColor: "#d32f2f",
            color: "white",
            textTransform: "none",
            fontSize: "0.7rem",
            "&:hover": { backgroundColor: "#b71c1c" }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleUpdate}
          variant="contained" 
          size="small"
          sx={{
            textTransform: "none",
            fontSize: "0.7rem",
          }}
        >
          {isPayrollEntry ? "Update & Next" : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PayrollItemTypeMapping.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdateAndNext: PropTypes.func.isRequired,
  source: PropTypes.string,
  rowData: PropTypes.object,
};

export default PayrollItemTypeMapping;
              